import { Accordion, EHeadingStyleVariant, ELinkVariant, Heading, Link } from '@outdoorsyco/bonfire';
import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import css from './FAQs.module.css';

type TFAQ = {
  question: string;
  answer: string | React.ReactNode;
};

export const FAQs = () => {
  const intl = useIntl();
  const faqs: TFAQ[] = [
    {
      question: intl.formatMessage({
        defaultMessage: 'What exactly is an Outdoorsy Escape?',
        id: 'WjA4En',
      }),
      answer: intl.formatMessage({
        defaultMessage:
          "It's your ticket to hassle-free outdoor adventure—a premium RV and top-rated campsite bundled into one simple booking. Enjoy hotel-grade amenities, breathtaking views, and resort features like swimming pools, hot tubs, mini-golf, and plenty of family-friendly activities.",
        id: 'h+efjC',
      }),
    },
    {
      question: intl.formatMessage({
        defaultMessage: 'Are these Escapes good for families?',
        id: 'tf/zVD',
      }),
      answer: intl.formatMessage({
        defaultMessage:
          'Absolutely! Our handpicked resorts offer activities that keep everyone smiling, from pickleball courts to water parks. Many locations are just a short drive from attractions like Margaritaville, national parks, and other must-see destinations.',
        id: 'k1GW9h',
      }),
    },
    {
      question: intl.formatMessage({
        defaultMessage: 'Where can I escape to with Outdoorsy Escapes?',
        id: 'Piy83+',
      }),
      answer: intl.formatMessage({
        defaultMessage:
          'Find what moves you with resorts across the country offering that perfect blend of outdoor adventure and creature comforts.',
        id: 'Ggza5v',
      }),
    },
    {
      question: intl.formatMessage({
        defaultMessage: "What's inside the RVs for Outdoorsy Escapes?",
        id: '10fUR+',
      }),
      answer: intl.formatMessage({
        defaultMessage:
          'Your home-on-wheels comes equipped with comfortable sleeping areas, kitchen facilities, bathrooms, and living spaces. Many feature special touches like outdoor kitchens, hammocks, and WiFi.',
        id: '8zq2pu',
      }),
    },
    {
      question: intl.formatMessage({
        defaultMessage: 'Is there a minimum stay for Escapes?',
        id: 'fZFcOi',
      }),
      answer: intl.formatMessage({
        defaultMessage:
          'Yes—minimum stay requirements vary by location, but most RV rentals require at least a 2 night minimum. All the details are clearly shown during the booking process.',
        id: 'k45t17',
      }),
    },
    {
      question: intl.formatMessage({
        defaultMessage: 'What if my plans change?',
        id: 'EEtLxK',
      }),
      answer: intl.formatMessage(
        {
          defaultMessage:
            'Life happens! All guests will be held to Outdoorsy’s Moderate Cancellation {policyLink}. For help with cancellations, reach out to {emailLink}.',
          id: '024nNi',
        },
        {
          policyLink: (
            <Link
              variant={ELinkVariant.Underline}
              href="https://www.outdoorsy.com/help/what-is-the-cancellation-policy"
              target="_blank"
              rel="noopener noreferrer">
              Policy
            </Link>
          ),
          emailLink: (
            <Link
              variant={ELinkVariant.Underline}
              href="mailto:Outdoorsyescapes@outdoorsy.co"
              target="_blank"
              rel="noopener noreferrer">
              Outdoorsyescapes@outdoorsy.co
            </Link>
          ),
        },
      ),
    },
    {
      question: intl.formatMessage({
        defaultMessage: 'Can my dog come too?',
        id: 'Gwn3Cu',
      }),
      answer: intl.formatMessage({
        defaultMessage:
          'Fresh air is good for furry friends too! Our Escapes (RV + Stays) include pet-friendly RV resorts.',
        id: 'jS5777',
      }),
    },
    {
      question: intl.formatMessage({
        defaultMessage: 'Who do I contact if I need help with my Escape?',
        id: 'LK+wpi',
      }),
      answer: intl.formatMessage(
        {
          defaultMessage:
            'Our Escapes customer support team is available daily from 9 AM to 6 PM to ensure your outdoor experience stays worry-free. All contact information comes with your booking confirmation. Reach out to {emailLink} for more details.',
          id: '6BGP05',
        },
        {
          emailLink: (
            <Link
              variant={ELinkVariant.Underline}
              href="mailto:Outdoorsyescapes@outdoorsy.co"
              target="_blank"
              rel="noopener noreferrer">
              Outdoorsyescapes@outdoorsy.co
            </Link>
          ),
        },
      ),
    },
    {
      question: intl.formatMessage({
        defaultMessage: 'How does check-in work for my Escape?',
        id: 'D9Woml',
      }),
      answer: intl.formatMessage({
        defaultMessage:
          'Simply arrive at your reserved check-in time and head to the front desk—your reservation will be under your name. Your RV host will meet you at your site (look for the Outdoorsy sign) to introduce you to your home for the stay.',
        id: 'A3nIJE',
      }),
    },
  ];

  return (
    <div className="md:px-16">
      <Heading variant={EHeadingStyleVariant.H2} className="font-normal text-800 lg:text-900">
        <FormattedMessage defaultMessage="FAQ's" id="Mp68c+" />
      </Heading>
      <Accordion className={css.accordion}>
        {faqs.map(faq => (
          <Accordion.Item key={faq.question} title={faq.question} divider>
            <div>{faq.answer}</div>
          </Accordion.Item>
        ))}
      </Accordion>
    </div>
  );
};
